import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import Select from 'react-select';
import {
  Button,
  Card,
  Col,
  Form as BsForm,
  Row,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { acertarContaService } from '~/Services/Service';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import Loading from '~/Components/Loading';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import { acertarContaSchema } from './validationSchema';

const valoresIniciais = {
  codigoGe: '',
  codigoOrigem: '',
  codigoDestino: '',
  codigoCentroCusto: '',
};

const AcertarConta = () => {
  const empreendimentosRedux = useSelector((state) => state.empreendimentos);
  const opcoesGe = useMemo(() => {
    const lista =
      empreendimentosRedux && empreendimentosRedux.sort
        ? empreendimentosRedux.sort
        : [];
    return lista.map((emp) => ({
      value: emp.codigo,
      label: `${emp.codigo} - ${emp.descricao}`,
    }));
  }, [empreendimentosRedux]);

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [novaContaLabel, setNovaContaLabel] = useState('');

  const onBlurOrigem = async (values, setFieldError) => {
    if (!values.codigoOrigem || !values.codigoGe) return;
    try {
      const r = await acertarContaService.validarOrigem({
        codigo_ge: Number(values.codigoGe),
        codigo_origem: Number(values.codigoOrigem),
      });
      if (r && r.valida === false) {
        setFieldError('codigoOrigem', r.mensagem || 'Conta inválida.');
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  };

  const onBlurDestino = async (values, setFieldError) => {
    if (!values.codigoDestino || !values.codigoGe) return;
    try {
      const r = await acertarContaService.validarDestino({
        codigo_ge: Number(values.codigoGe),
        codigo_destino: Number(values.codigoDestino),
      });
      setNovaContaLabel(r && r.nova_conta ? 'Inclusão nova conta' : '');
      if (r && r.valida === false) {
        setFieldError('codigoDestino', r.mensagem || 'Conta destino inválida.');
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  };

  const onBlurCC = async (values, setFieldError) => {
    if (!values.codigoCentroCusto) return;
    try {
      const r = await acertarContaService.validarCC({
        codigo_centro_custo: Number(values.codigoCentroCusto),
      });
      if (r && r.valida === false) {
        setFieldError(
          'codigoCentroCusto',
          r.mensagem || 'Centro de custo inválido.'
        );
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  };

  const handleSubmit = (values) => {
    setPendingValues(values);
    setShowConfirm(true);
  };

  const handleConfirmClose = async (confirmado) => {
    setShowConfirm(false);
    if (!confirmado || !pendingValues) {
      setPendingValues(null);
      return;
    }
    setLoading(true);
    try {
      const resp = await acertarContaService.executar({
        codigo_ge: Number(pendingValues.codigoGe),
        codigo_origem: Number(pendingValues.codigoOrigem),
        codigo_destino: Number(pendingValues.codigoDestino),
        codigo_centro_custo: pendingValues.codigoCentroCusto
          ? Number(pendingValues.codigoCentroCusto)
          : null,
      });
      toast.success(
        (resp && resp.mensagem) || 'Acerto de contas concluído com sucesso.'
      );
      setPendingValues(null);
      setNovaContaLabel('');
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-3">
      <Card>
        <Card.Header>
          <h4 className="mb-0">Acerta contas de despesas/receitas</h4>
        </Card.Header>
        <Card.Body>
          {loading && <Loading label="Executando acerto..." />}
          <Formik
            initialValues={valoresIniciais}
            validationSchema={acertarContaSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, setFieldError, resetForm }) => (
              <Form>
                <Row>
                  <Col xs={12} md={6}>
                    <BsForm.Label className="small mb-1">
                      Empreendimento
                    </BsForm.Label>
                    <Select
                      options={opcoesGe}
                      value={
                        opcoesGe.find((o) => o.value === values.codigoGe) ||
                        null
                      }
                      onChange={(opt) => {
                        setFieldValue('codigoGe', opt ? opt.value : '');
                        setNovaContaLabel('');
                      }}
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      placeholder="Selecione o Empreendimento"
                      isClearable
                      noOptionsMessage={() => 'Nenhum empreendimento'}
                    />
                    <ErrorMessage
                      name="codigoGe"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col xs={12} md={4}>
                    <BsForm.Label className="small mb-1">
                      Código Atual
                    </BsForm.Label>
                    <Field
                      name="codigoOrigem"
                      type="number"
                      className="form-control form-control-sm"
                      onBlur={() => onBlurOrigem(values, setFieldError)}
                      disabled={loading}
                    />
                    <ErrorMessage
                      name="codigoOrigem"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <BsForm.Label className="small mb-1">
                      Novo Código
                    </BsForm.Label>
                    <Field
                      name="codigoDestino"
                      type="number"
                      className="form-control form-control-sm"
                      onBlur={() => onBlurDestino(values, setFieldError)}
                      disabled={loading}
                    />
                    {novaContaLabel && (
                      <div className="text-info small mt-1">
                        {novaContaLabel}
                      </div>
                    )}
                    <ErrorMessage
                      name="codigoDestino"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <BsForm.Label className="small mb-1">
                      Centro de Custo (opcional)
                    </BsForm.Label>
                    <Field
                      name="codigoCentroCusto"
                      type="number"
                      className="form-control form-control-sm"
                      onBlur={() => onBlurCC(values, setFieldError)}
                      disabled={loading}
                    />
                    <ErrorMessage
                      name="codigoCentroCusto"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col xs={12} className="text-right">
                    <Button
                      variant="outline-secondary"
                      className="mr-2"
                      onClick={() => {
                        resetForm();
                        setNovaContaLabel('');
                      }}
                      disabled={loading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading}
                    >
                      Executar Acerto
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>

      <ModalConfirmacao
        show={showConfirm}
        onClose={handleConfirmClose}
        title="Confirma acerto de contas?"
        message={
          <>
            <p className="mb-2">
              <strong>Esta operação é irreversível.</strong>
            </p>
            <p className="mb-0">
              A conta origem será excluída permanentemente e todas as
              referências passarão a apontar para a conta destino.
            </p>
          </>
        }
        variant="danger"
      />
    </div>
  );
};

export default AcertarConta;
